<template>
    <div class="signature-block">
        <v-row dense>
            <v-col cols="12" sm="6">
                <v-text-field
                    v-model="local.name"
                    :label="labels.name"
                    prepend-icon="mdi-account"
                    :rules="[rules.required]"
                    hide-details="auto"
                    dense
                    :readonly="lockName"
                    :disabled="lockName"
                    @input="emitChange"
                />
            </v-col>
            <v-col cols="12" sm="6">
                <v-text-field
                    v-model="local.documentId"
                    :label="labels.documentId"
                    prepend-icon="mdi-card-account-details-outline"
                    :rules="[rules.required]"
                    hide-details="auto"
                    dense
                    @input="emitChange"
                />
            </v-col>
        </v-row>

        <p class="text-caption font-weight-medium mt-4 mb-2">
            {{ labels.signature }}
        </p>
        <v-btn-toggle
            v-model="local.signatureMode"
            mandatory
            dense
            class="mb-3"
            @change="onModeChange"
        >
            <v-btn small value="draw">
                <v-icon left small>mdi-draw</v-icon>
                {{ labels.draw }}
            </v-btn>
            <v-btn small value="upload">
                <v-icon left small>mdi-image-plus</v-icon>
                {{ labels.uploadImage }}
            </v-btn>
        </v-btn-toggle>

        <div v-if="local.signatureMode === 'draw'" class="signature-draw-area">
            <canvas
                ref="canvas"
                class="signature-canvas"
                width="400"
                height="150"
                @mousedown="startDrawing"
                @mousemove="draw"
                @mouseup="stopDrawing"
                @mouseleave="stopDrawing"
                @touchstart.prevent="startDrawingTouch"
                @touchmove.prevent="drawTouch"
                @touchend.prevent="stopDrawing"
            />
            <div class="d-flex mt-2">
                <v-btn small text color="secondary" @click="clearCanvas">
                    <v-icon left small>mdi-eraser</v-icon>
                    {{ labels.clear }}
                </v-btn>
            </div>
        </div>

        <div v-else class="signature-upload-area">
            <div v-if="local.signaturePreview" class="signature-preview-wrap">
                <v-img
                    :src="local.signaturePreview"
                    max-height="150"
                    max-width="400"
                    contain
                    class="signature-preview"
                />
                <v-btn
                    icon
                    small
                    color="red"
                    class="signature-preview-remove"
                    @click="removeUploadedSignature"
                >
                    <v-icon>mdi-delete</v-icon>
                </v-btn>
            </div>
            <v-btn
                v-else
                outlined
                small
                color="primary"
                :loading="uploadingSignature"
                @click="triggerUpload"
            >
                <v-icon left small>mdi-upload</v-icon>
                {{ labels.selectImage }}
            </v-btn>
            <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="d-none"
                @change="onFileSelected"
            />
        </div>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import { saveFile } from '@/services/storage/saveFile.js'

const emptySigner = () => ({
    name: '',
    documentId: '',
    signatureMode: 'draw',
    signatureData: null,
    signaturePreview: null,
    signatureFile: null,
})

export default {
    name: 'SignatureBlock',
    props: {
        value: {
            type: Object,
            default: () => emptySigner(),
        },
        label: {
            type: String,
            required: true,
        },
        hideTitle: {
            type: Boolean,
            default: false,
        },
        lockName: {
            type: Boolean,
            default: false,
        },
        userId: {
            type: String,
            default: '',
        },
        companyId: {
            type: String,
            default: '',
        },
        persistSignatureOnUpload: {
            type: Boolean,
            default: false,
        },
        language: {
            type: String,
            default: 'English',
        },
    },
    data() {
        return {
            local: { ...emptySigner(), ...this.value },
            isDrawing: false,
            lastX: 0,
            lastY: 0,
            hasDrawn: false,
            uploadingSignature: false,
        }
    },
    computed: {
        isSpanish() {
            return (
                String(this.language || '')
                    .trim()
                    .toLowerCase() === 'spanish'
            )
        },
        labels() {
            if (this.isSpanish) {
                return {
                    name: 'Nombre',
                    documentId: 'Documento de identidad',
                    signature: 'Firma',
                    draw: 'Dibujar',
                    uploadImage: 'Subir imagen',
                    clear: 'Limpiar',
                    selectImage: 'Seleccionar imagen',
                    required: 'Requerido',
                }
            }
            return {
                name: 'Name',
                documentId: 'ID document',
                signature: 'Signature',
                draw: 'Draw',
                uploadImage: 'Upload image',
                clear: 'Clear',
                selectImage: 'Select image',
                required: 'Required',
            }
        },
        rules() {
            return {
                required: v => !!v || this.labels.required,
            }
        },
    },
    watch: {
        value: {
            deep: true,
            handler(val) {
                this.local = { ...emptySigner(), ...val }
                if (this.local.signatureMode === 'draw') {
                    this.$nextTick(() => this.initCanvas())
                }
            },
        },
    },
    mounted() {
        this.initCanvas()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        initCanvas() {
            const canvas = this.$refs.canvas
            if (!canvas) return
            const ctx = canvas.getContext('2d')
            ctx.strokeStyle = '#000'
            ctx.lineWidth = 2
            ctx.lineCap = 'round'
            ctx.lineJoin = 'round'
            if (
                this.local.signatureData &&
                this.local.signatureMode === 'draw'
            ) {
                this.restoreCanvasFromData(this.local.signatureData)
                this.hasDrawn = true
            }
        },
        refreshCanvas() {
            this.$nextTick(() => {
                this.initCanvas()
            })
        },
        getCanvasContext() {
            const canvas = this.$refs.canvas
            if (!canvas) return null
            return canvas.getContext('2d')
        },
        getCanvasCoords(event) {
            const canvas = this.$refs.canvas
            const rect = canvas.getBoundingClientRect()
            const scaleX = canvas.width / rect.width
            const scaleY = canvas.height / rect.height
            return {
                x: (event.clientX - rect.left) * scaleX,
                y: (event.clientY - rect.top) * scaleY,
            }
        },
        getTouchCoords(event) {
            const canvas = this.$refs.canvas
            const rect = canvas.getBoundingClientRect()
            const touch = event.touches[0]
            const scaleX = canvas.width / rect.width
            const scaleY = canvas.height / rect.height
            return {
                x: (touch.clientX - rect.left) * scaleX,
                y: (touch.clientY - rect.top) * scaleY,
            }
        },
        startDrawing(event) {
            this.isDrawing = true
            const { x, y } = this.getCanvasCoords(event)
            this.lastX = x
            this.lastY = y
        },
        startDrawingTouch(event) {
            this.isDrawing = true
            const { x, y } = this.getTouchCoords(event)
            this.lastX = x
            this.lastY = y
        },
        draw(event) {
            if (!this.isDrawing) return
            const ctx = this.getCanvasContext()
            if (!ctx) return
            const { x, y } = this.getCanvasCoords(event)
            ctx.beginPath()
            ctx.moveTo(this.lastX, this.lastY)
            ctx.lineTo(x, y)
            ctx.stroke()
            this.lastX = x
            this.lastY = y
            this.hasDrawn = true
        },
        drawTouch(event) {
            if (!this.isDrawing) return
            const ctx = this.getCanvasContext()
            if (!ctx) return
            const { x, y } = this.getTouchCoords(event)
            ctx.beginPath()
            ctx.moveTo(this.lastX, this.lastY)
            ctx.lineTo(x, y)
            ctx.stroke()
            this.lastX = x
            this.lastY = y
            this.hasDrawn = true
        },
        stopDrawing() {
            if (!this.isDrawing) return
            this.isDrawing = false
            if (this.hasDrawn) {
                this.saveCanvasSignature()
            }
        },
        saveCanvasSignature() {
            const canvas = this.$refs.canvas
            if (!canvas) return
            this.local.signatureData = canvas.toDataURL('image/png')
            this.local.signatureFile = null
            this.emitChange()
        },
        restoreCanvasFromData(dataUrl) {
            const canvas = this.$refs.canvas
            if (!canvas || !dataUrl) return
            const ctx = canvas.getContext('2d')
            const img = new Image()
            img.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height)
                ctx.drawImage(img, 0, 0)
            }
            img.src = dataUrl
        },
        clearCanvas() {
            const canvas = this.$refs.canvas
            if (!canvas) return
            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            this.hasDrawn = false
            this.local.signatureData = null
            this.local.signaturePreview = null
            this.emitChange()
        },
        onModeChange() {
            if (this.local.signatureMode === 'upload') {
                return
            }
            this.local.signatureData = null
            this.local.signaturePreview = null
            this.local.signatureFile = null
            this.hasDrawn = false
            this.$nextTick(() => {
                this.initCanvas()
            })
            this.emitChange()
        },
        triggerUpload() {
            this.$refs.fileInput.click()
        },
        async onFileSelected(event) {
            const file = event.target.files?.[0]
            if (!file || !file.type.startsWith('image/')) return
            try {
                this.uploadingSignature = true
                if (
                    this.local.signaturePreview &&
                    this.local.signaturePreview.startsWith('blob:')
                ) {
                    URL.revokeObjectURL(this.local.signaturePreview)
                }
                this.local.signatureFile = file
                this.local.signaturePreview = URL.createObjectURL(file)
                const reader = new FileReader()
                reader.onload = e => {
                    this.local.signatureData = e.target.result
                    this.emitChange()
                }
                reader.readAsDataURL(file)

                if (
                    this.persistSignatureOnUpload &&
                    this.userId &&
                    this.companyId
                ) {
                    await this.persistDefaultSignature(file)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.uploadingSignature = false
                event.target.value = ''
            }
        },
        dataURLtoFile(dataUrl, filename) {
            const arr = dataUrl.split(',')
            const mime = arr[0].match(/:(.*?);/)[1]
            const bstr = atob(arr[1])
            let n = bstr.length
            const u8arr = new Uint8Array(n)
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n)
            }
            return new File([u8arr], filename, { type: mime })
        },
        async persistDefaultSignature(fileOverride = null) {
            if (
                !this.persistSignatureOnUpload ||
                !this.userId ||
                !this.companyId
            ) {
                return false
            }

            let file = fileOverride || this.local.signatureFile
            if (!file && this.local.signatureData) {
                if (this.local.signatureData.startsWith('data:')) {
                    file = this.dataURLtoFile(
                        this.local.signatureData,
                        'signature.png'
                    )
                } else if (
                    typeof this.local.signatureData === 'string' &&
                    this.local.signatureData.startsWith('http')
                ) {
                    // Already persisted as the user's default signature URL
                    await API.updateOwnSignature({
                        signature: true,
                        documentId: this.local.documentId || null,
                    })
                    this.$emit('signature-persisted')
                    return true
                }
            }
            if (!file) return false

            await saveFile(
                file,
                `${this.companyId}/usersSignatures`,
                this.userId
            )
            await API.updateOwnSignature({
                signature: true,
                documentId: this.local.documentId || null,
            })
            this.$emit('signature-persisted')
            return true
        },
        removeUploadedSignature() {
            if (
                this.local.signaturePreview &&
                this.local.signaturePreview.startsWith('blob:')
            ) {
                URL.revokeObjectURL(this.local.signaturePreview)
            }
            this.local.signatureFile = null
            this.local.signaturePreview = null
            this.local.signatureData = null
            this.emitChange()
        },
        emitChange() {
            this.$emit('input', { ...this.local })
        },
    },
    beforeDestroy() {
        if (
            this.local.signaturePreview &&
            this.local.signaturePreview.startsWith('blob:')
        ) {
            URL.revokeObjectURL(this.local.signaturePreview)
        }
    },
}
</script>

<style scoped>
.signature-canvas {
    width: 100%;
    max-width: 400px;
    height: 150px;
    border: 1px solid #bdbdbd;
    border-radius: 4px;
    background: #fff;
    cursor: crosshair;
    touch-action: none;
}

.signature-preview-wrap {
    position: relative;
    display: inline-block;
}

.signature-preview {
    border: 1px solid #bdbdbd;
    border-radius: 4px;
}

.signature-preview-remove {
    position: absolute;
    top: 4px;
    right: 4px;
}
</style>
